import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { theme, typography, spacing, borderRadius, mobileSpacing } from '../styles/theme';
import * as Sentry from '@sentry/react';

export default function ConversationDrawer({ 
  user, 
  currentConversationId,
  onSelectConversation, 
  onNewConversation,
  isOpen,
  onClose
}) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const menuRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    if (!user || !isOpen) return;
    fetchConversations();
  }, [user, isOpen, showFavorites]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    };

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (showFavorites) params.append('favorites', 'true');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversations?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationDrawer', action: 'fetchConversations' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConversations();
  };

  const handleSelectConversation = (convId) => {
    onSelectConversation(convId);
    onClose();
  };

  const handleNewConversation = () => {
    onNewConversation();
    onClose();
  };

  const handleDelete = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;

    try {
      const token = await user.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.filter(c => c._id !== convId));
      if (currentConversationId === convId) {
        onNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationDrawer', action: 'deleteConversation' }
      });
    }
  };

  const handleToggleFavorite = async (convId, e) => {
    e.stopPropagation();
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.map(c => 
        c._id === convId ? { ...c, isFavorite: response.data.isFavorite } : c
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationDrawer', action: 'toggleFavorite' }
      });
    }
  };

  const handleStartRename = (conv, e) => {
    e.stopPropagation();
    setEditingId(conv._id);
    setEditTitle(conv.title);
  };

  const handleSaveRename = async (convId, e) => {
    e.stopPropagation();
    if (!editTitle.trim()) return;

    try {
      const token = await user.getIdToken();
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}`,
        { title: editTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.map(c =>
        c._id === convId ? { ...c, title: editTitle } : c
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Error renaming conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationDrawer', action: 'renameConversation' }
      });
    }
  };

  const handleCancelRename = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditTitle('');
  };

  const handleExportConversation = async (conv, e) => {
    e.stopPropagation();

    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversations/${conv._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const messages = response.data.messages.map(msg => ({
        sender: msg.role === 'user' ? 'user' : 'bot',
        text: msg.content
      }));

      const { exportConversationToPDF } = await import('../utils/exportPDF');
      exportConversationToPDF(messages, conv.title);
    } catch (error) {
      console.error('Error exporting conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationDrawer', action: 'exportConversation'}
      });
      alert('Failed to export conversation');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1001,
        }}
      />
      
      {/* Drawer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: '85%',
        maxWidth: '320px',
        backgroundColor: theme.bg.primary,
        zIndex: 1002,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadow.large,
        animation: 'slideIn 0.2s ease-out',
      }}>
        {/* Header */}
        <div style={{
          padding: mobileSpacing.md,
          borderBottom: `1px solid ${theme.border.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.bg.dark,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            color: theme.text.inverse,
          }}>
            Conversations
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.text.inverse,
              padding: mobileSpacing.sm,
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* New Conversation Button */}
        <div style={{ padding: mobileSpacing.md }}>
          <button
            onClick={handleNewConversation}
            style={{
              width: '100%',
              padding: mobileSpacing.md,
              backgroundColor: theme.brand.primary,
              color: 'white',
              border: 'none',
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              fontWeight: typography.fontWeight.medium,
              fontSize: typography.fontSize.base,
              minHeight: '48px',
            }}
          >
            + New Conversation
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: `0 ${mobileSpacing.md}` }}>
          <form onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: mobileSpacing.sm,
                border: `1px solid ${theme.border.light}`,
                borderRadius: borderRadius.sm,
                fontSize: typography.fontSize.base,
                boxSizing: 'border-box',
                minHeight: '44px',
              }}
            />
          </form>
          
          {/* Favorites filter */}
          <button
            onClick={() => setShowFavorites(!showFavorites)}
            style={{
              marginTop: mobileSpacing.sm,
              padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
              backgroundColor: showFavorites ? theme.brand.primary : 'transparent',
              color: showFavorites ? 'white' : theme.text.secondary,
              border: `1px solid ${theme.border.light}`,
              borderRadius: borderRadius.sm,
              cursor: 'pointer',
              fontSize: typography.fontSize.sm,
              minHeight: '40px',
            }}
          >
            ⭐ Favorites Only
          </button>
        </div>

        {/* Conversation List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: mobileSpacing.md,
        }}>
          {loading ? (
            <div style={{ 
              padding: mobileSpacing.lg, 
              textAlign: 'center',
              color: theme.text.secondary 
            }}>
              Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ 
              padding: mobileSpacing.lg, 
              textAlign: 'center',
              color: theme.text.secondary,
              fontSize: typography.fontSize.base,
            }}>
              {showFavorites ? 'No favorites yet' : 'No conversations yet'}
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv._id}
                onClick={() => handleSelectConversation(conv._id)}
                style={{
                  padding: mobileSpacing.md,
                  marginBottom: mobileSpacing.sm,
                  backgroundColor: currentConversationId === conv._id 
                    ? theme.brand.light 
                    : theme.bg.secondary,
                  borderRadius: borderRadius.md,
                  cursor: 'pointer',
                  minHeight: '60px',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Title - editable or display */}
                    {editingId === conv._id ? (
                      <div style={{ display: 'flex', gap: mobileSpacing.xs }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(conv._id, e);
                            if (e.key === 'Escape') handleCancelRename(e);
                          }}
                          autoFocus
                          style={{
                            flex: 1,
                            padding: mobileSpacing.sm,
                            fontSize: typography.fontSize.base,
                            border: `1px solid ${theme.border.light}`,
                            borderRadius: borderRadius.sm,
                            minHeight: '44px',
                          }}
                        />
                        <button
                          onClick={(e) => handleSaveRename(conv._id, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: mobileSpacing.sm,
                            color: theme.status.success,
                            fontSize: typography.fontSize.lg,
                            minWidth: '44px',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelRename}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: mobileSpacing.sm,
                            color: theme.status.error,
                            fontSize: typography.fontSize.lg,
                            minWidth: '44px',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{
                        fontSize: typography.fontSize.base,
                        fontWeight: typography.fontWeight.medium,
                        color: theme.text.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {conv.isFavorite && '⭐ '}{conv.title}
                      </div>
                    )}
                    
                    {/* Date and message count - only show when not editing */}
                    {editingId !== conv._id && (
                      <div style={{
                        fontSize: typography.fontSize.sm,
                        color: theme.text.tertiary,
                        marginTop: mobileSpacing.xs,
                      }}>
                        {formatDate(conv.updatedAt)} · {conv.messageCount} messages
                      </div>
                    )}
                  </div>
                  
                  {/* Meatball menu - only show if not editing */}
                  <div style={{ position: 'relative' }} ref={openMenuId === conv._id ? menuRef : null}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === conv._id ? null : conv._id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: spacing.xs,
                        fontSize: typography.fontSize.lg,
                        color: theme.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px',
                        minHeight: '24px',
                      }}
                      title="Options"
                    >
                      ⋮
                    </button>

                    {/* Dropdown menu */}
                    {openMenuId === conv._id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          backgroundColor: 'white',
                          border: `1px solid ${theme.border.light}`,
                          borderRadius: borderRadius.md,
                          boxShadow: theme.shadow.medium,
                          zIndex: 1000,
                          minWidth: '160px',
                          marginTop: spacing.xs,
                        }}
                      >
                        {/* Rename */}
                        <button
                          onClick={(e) => {
                            handleStartRename(conv, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>✏️</span>
                          <span>Rename</span>
                        </button>

                        {/* Favorite/Unfavorite */}
                        <button
                          onClick={(e) => {
                            handleToggleFavorite(conv._id, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>{conv.isFavorite ? '⭐' : '☆'}</span>
                          <span>{conv.isFavorite ? 'Unfavorite' : 'Favorite'}</span>
                        </button>

                        {/* Export to PDF */}
                        <button
                          onClick={(e) => {
                            handleExportConversation(conv, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>📄</span>
                          <span>Export PDF</span>
                        </button>

                        {/* Divider */}
                        <div style={{
                          height: '1px',
                          backgroundColor: theme.border.light,
                          margin: `${spacing.xs} 0`,
                        }} />

                        {/* Delete */}
                        <button
                          onClick={(e) => {
                            handleDelete(conv._id, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.status.error,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFE5E5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>🗑</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* CSS Animation */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}